import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { NumberInputRow } from "@/components/form/NumberInputRow";
import { AnimatedFormError } from '@/components/form/AnimatedFormError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfigSection } from '@/components/config_forms/shared/ConfigSection';
import { ExpenseCategoryRow } from '@/components/config_forms/expense/ExpenseCategoryRow';
import { createCustomExpenseCategory } from '@/constants/expenses';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE_EXPO_OUT, DURATION_MEDIUM } from '@/lib/motion';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { Briefcase, Receipt, Plus } from 'lucide-react';
import type { ExpenseCategory } from '@/types/ExpenseCategory';

interface TravelConfigFormProps {
  // Data
  title: string;
  description: string;
  needsTravel: boolean;
  usesGsaRate: boolean;
  applyPerDiem: boolean;
  multiplierAppliesToOverages: boolean;

  // Handlers
  onNeedsTravelChange: (checked: boolean) => void;
  onApplyPerDiemChange: (checked: boolean) => void;
  onUsesGsaRateChange: (checked: boolean) => void;
  onMultiplierAppliesToOveragesChange: (checked: boolean) => void;

  // Props for controlled inputs
  perDiemInputProps: Record<string, unknown>;
  travelMultiplierInputProps: Record<string, unknown>;

  // Validation
  perDiemError?: React.ReactNode;
  travelMultiplierError?: React.ReactNode;

  // New: distance threshold
  travelDistanceThreshold: number | null;
  onTravelDistanceThresholdChange: (value: number | null) => void;
  travelDistanceThresholdInputProps?: Record<string, unknown>;
  travelDistanceThresholdError?: React.ReactNode;

  // New: travel expense categories (inline section)
  travelExpenseCategories: ExpenseCategory[];
  onTravelExpenseCategoriesChange: (categories: ExpenseCategory[]) => void;

  // Existing (kept)
  isCustomized?: boolean;
  onRevert?: () => void;
  context?: 'user' | 'client';
  noCard?: boolean;
}

const TravelConfigFormComponent: React.FC<TravelConfigFormProps> = ({
  title,
  description,
  needsTravel,
  usesGsaRate,
  applyPerDiem,
  multiplierAppliesToOverages,
  onNeedsTravelChange,
  onApplyPerDiemChange,
  onUsesGsaRateChange,
  onMultiplierAppliesToOveragesChange,
  perDiemInputProps,
  travelMultiplierInputProps,
  perDiemError,
  travelMultiplierError,
  travelDistanceThreshold,
  onTravelDistanceThresholdChange,
  travelDistanceThresholdInputProps,
  travelDistanceThresholdError,
  travelExpenseCategories,
  onTravelExpenseCategoriesChange,
  isCustomized,
  onRevert,
  context = 'user',
  noCard = false,
}) => {
  const [travelExpensesOpen, setTravelExpensesOpen] = useState(false);

  const handleCategoryChange = (updated: ExpenseCategory) => {
    onTravelExpenseCategoriesChange(
      travelExpenseCategories.map((c) => (c.id === updated.id ? updated : c))
    );
  };

  const handleDeleteCategory = (id: string) => {
    onTravelExpenseCategoriesChange(travelExpenseCategories.filter((c) => c.id !== id));
  };

  const handleAddTravelCategory = () => {
    onTravelExpenseCategoriesChange([
      ...travelExpenseCategories,
      createCustomExpenseCategory({ scope: 'travel_only', name: 'New Travel Category' }),
    ]);
  };

  const content = (
    <div className="space-y-6">
      <div className="space-y-1 text-center">
        <div className="flex items-center justify-center gap-3">
          <h3 className="text-lg font-medium text-foreground">{title}</h3>
          {isCustomized && <Badge variant="default">{context === 'client' ? 'Client' : 'Profile'} Override</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="space-y-6">
        <CardContent className="space-y-6 p-0">
          {/* Section 1: Travel Defaults */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-950/30">
                <Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h4 className="text-base font-medium text-foreground">Travel Billing</h4>
                <p className="text-sm text-muted-foreground">Enable this to configure policies for travel bookings.</p>
              </div>
            </div>
            <Switch checked={needsTravel} onCheckedChange={onNeedsTravelChange} />
          </div>

          <AnimatedCollapse open={needsTravel}>
                <div className="space-y-4 animate-fade-in pt-4">
                  {/* Distance Threshold */}
                  <NumberInputRow
                    id="travelDistanceThreshold"
                    label="Travel Distance Threshold"
                    description="Bookings beyond this distance are flagged as travel by default"
                    unit="mi"
                    unitPosition="right"
                    placeholder="e.g. 100"
                    inputProps={{
                      ...(travelDistanceThresholdInputProps || {
                        value: travelDistanceThreshold ?? '',
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                          onTravelDistanceThresholdChange(e.target.value ? parseInt(e.target.value, 10) : null),
                      }),
                      min: 0,
                      step: 10,
                      className: "w-[100px] text-right",
                    }}
                    errorElement={travelDistanceThresholdError ? <AnimatedFormError>{travelDistanceThresholdError}</AnimatedFormError> : undefined}
                  />

                  {/* Section 2: Travel Day Rate */}
                  <Card className="p-4 bg-transparent">
                    <CardContent className="p-0 space-y-4">
                      <NumberInputRow
                        id="travelDayRateMultiplier"
                        label="Travel Shift Rate Multiplier"
                        description={<span className="text-xs">Multiplier of your full rate for travel days</span>}
                        unit="×"
                        unitPosition="right"
                        placeholder="e.g. 0.75"
                        inputProps={{
                          ...travelMultiplierInputProps,
                          min: 0,
                          minValue: 0,
                          max: 10,
                          maxValue: 10,
                          step: 0.05,
                          className: cn(travelMultiplierInputProps.className as string, "w-[100px] text-right"),
                        }}
                        errorElement={<AnimatedFormError>{travelMultiplierError}</AnimatedFormError>}
                      />

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex flex-col gap-1">
                          <Label htmlFor="multiplierAppliesToOverages" className={cn(fieldLabelClass, "cursor-pointer")}>
                            Apply Travel Multiplier to Overages
                          </Label>
                          <p className={cn(fieldDescriptionClass, "mt-1")}>
                            Apply travel shift rate multiplier to overtime hours.
                          </p>
                        </div>
                        <Switch
                          id="multiplierAppliesToOverages"
                          checked={multiplierAppliesToOverages || false}
                          onCheckedChange={onMultiplierAppliesToOveragesChange}
                          disabled={perDiemInputProps.disabled as boolean}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Section 3: Per Diem */}
                  <Card className="p-4 bg-transparent">
                    <CardContent className="p-0 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-1">
                          <Label htmlFor="apply-per-diem" className={cn(fieldLabelClass, "cursor-pointer")}>
                            Apply Per Diem
                          </Label>
                          <p className={fieldDescriptionClass}>
                            Provide a daily allowance for meals & incidentals on travel days
                          </p>
                        </div>
                        <Switch
                          id="apply-per-diem"
                          checked={applyPerDiem}
                          onCheckedChange={onApplyPerDiemChange}
                        />
                      </div>

                      <AnimatedCollapse open={applyPerDiem} className="pl-4 border-l-2 border-muted">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <div className="flex flex-col gap-1">
                                  <Label htmlFor="use-gsa-rates" className={cn(fieldLabelClass, "cursor-pointer")}>
                                    Use GSA Rates
                                  </Label>
                                  <p className={fieldDescriptionClass}>
                                    Federal per diem rates by location
                                  </p>
                                </div>
                                <Checkbox
                                  id="use-gsa-rates"
                                  checked={usesGsaRate}
                                  onCheckedChange={onUsesGsaRateChange}
                                />
                              </div>
                              <NumberInputRow
                                id="perDiemBaseRate"
                                label={
                                  <AnimatePresence mode="wait">
                                    <motion.span
                                      key={usesGsaRate ? "minimum" : "rate"}
                                      initial={{ opacity: 0, y: -10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: 10 }}
                                      transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
                                    >
                                      {usesGsaRate ? "Minimum Per Diem" : "Per Diem Rate"}
                                    </motion.span>
                                  </AnimatePresence>
                                }
                                description={
                                  <AnimatePresence mode="wait">
                                    <motion.span
                                      key={usesGsaRate ? "minimum-desc" : "rate-desc"}
                                      initial={{ opacity: 0, y: -10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: 10 }}
                                      transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
                                      className="text-xs"
                                    >
                                      {usesGsaRate ?
                                        "The minimum daily allowance, if GSA rate is lower." :
                                        "Daily allowance for meals & incidentals"
                                      }
                                    </motion.span>
                                  </AnimatePresence>
                                }
                                unit="$"
                                unitPosition="left"
                                placeholder="e.g. 60"
                                inputProps={{
                                  ...perDiemInputProps,
                                  min: 0,
                                  minValue: 0,
                                  step: 5,
                                  disabled: (perDiemInputProps.disabled as boolean),
                                  className: cn(perDiemInputProps.className as string, "w-[100px] text-right"),
                                }}
                                errorElement={<AnimatedFormError>{perDiemError}</AnimatedFormError>}
                              />
                            </div>
                      </AnimatedCollapse>
                    </CardContent>
                  </Card>

                  {/* Section 4: Travel Expenses (inline) */}
                  <ConfigSection
                    title="Travel Expenses"
                    description="Configure expense categories for travel bookings"
                    icon={Receipt}
                    isOpen={travelExpensesOpen}
                    onOpenChange={setTravelExpensesOpen}
                  >
                    <div className="space-y-3">
                      {travelExpenseCategories.map((cat) => (
                        <ExpenseCategoryRow
                          key={cat.id}
                          category={cat}
                          onChange={handleCategoryChange}
                          onDelete={() => handleDeleteCategory(cat.id)}
                          showScope={false}
                        />
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={handleAddTravelCategory}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Add Custom Travel Category
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        All expense categories are also available in Expense Policies
                      </p>
                    </div>
                  </ConfigSection>
                </div>
          </AnimatedCollapse>
        </CardContent>
      </div>
      {isCustomized && onRevert && (
        <div className="pt-4 border-t flex justify-center">
          <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
            Revert to Profile Defaults
          </Button>
        </div>
      )}
    </div>
  );

  if (noCard) {
    return (
      <div className="animate-fade-in w-full">
        {content}
      </div>
    );
  }

  return (
    <Card
      className={cn(
        "animate-fade-in shadow-sm rounded-xl p-4 sm:p-6",
        context === 'user' ? "w-full" : "max-w-3xl mx-auto"
      )}
    >
      {content}
    </Card>
  );
};

export const TravelConfigForm = React.memo(TravelConfigFormComponent);
TravelConfigForm.displayName = 'TravelConfigForm';
